import { useState, useEffect } from 'react';
import { RefreshCw, Check, AlertCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useRefresh } from '@/contexts/RefreshContext';

interface RefreshButtonProps {
  onRefreshComplete?: () => void;
  className?: string;
  iconOnly?: boolean;
}

export function RefreshButton({ onRefreshComplete, className, iconOnly = false }: RefreshButtonProps) {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [dataFreshness, setDataFreshness] = useState<{
    isStale: boolean;
    lastUpdate: Date | null;
    minutesSinceUpdate: number;
  } | null>(null);
  const { toast } = useToast();
  const { triggerRefresh, isRefreshing, dataStatus } = useRefresh();

  // Buscar última atualização ao montar o componente e usar dataStatus do contexto
  useEffect(() => {
    if (dataStatus) {
      setDataFreshness(dataStatus);
    } else {
      // Se não há dados do contexto, assumir que foi atualizado agora
      setDataFreshness({
        isStale: false,
        lastUpdate: new Date(),
        minutesSinceUpdate: 0
      });
    }
  }, [dataStatus]);

  // REMOVIDO: Auto-refresh causava chamadas duplicadas
  // O usuário deve clicar manualmente para atualizar


  const handleRefresh = async () => {
    setStatus('idle');

    try {
      await triggerRefresh();
      
      setStatus('success');
      
      // Chamar callback se fornecido
      onRefreshComplete?.();

      // Resetar status após 3 segundos
      setTimeout(() => setStatus('idle'), 3000);
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      setStatus('error');

      // Resetar status após 5 segundos
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const getIconColorBasedOnTime = () => {
    // Se houver erro, sempre vermelho
    if (status === 'error') {
      return 'text-destructive';
    }
    
    // Se estiver atualizando, manter cor padrão com animação
    if (isRefreshing) {
      return '';
    }
    
    // Se acabou de atualizar com sucesso, verde por 3 segundos
    if (status === 'success') {
      return 'text-success';
    }
    
    // Baseado no tempo desde última atualização
    if (dataFreshness) {
      if (dataFreshness.minutesSinceUpdate <= 10) {
        // Menos de 10 minutos - cor padrão (sem classe adicional)
        return '';
      } else if (dataFreshness.minutesSinceUpdate <= 30) {
        // Entre 10 e 30 minutos - amarelo
        return 'text-yellow-500';
      } else {
        // Mais de 30 minutos - vermelho
        return 'text-destructive';
      }
    }
    
    // Se não há dados de freshness, assumir que está atualizado (cor padrão)
    return '';
  };

  const getStatusIcon = () => {
    const colorClass = getIconColorBasedOnTime();
    
    if (isRefreshing) {
      return <RefreshCw className={cn("h-4 w-4 animate-spin", colorClass)} />;
    }
    
    // Se teve erro recente, mostrar ícone de alerta
    if (status === 'error') {
      return <AlertCircle className={cn("h-4 w-4", colorClass)} />;
    }
    
    // Se acabou de atualizar com sucesso, mostrar check
    if (status === 'success') {
      return <Check className={cn("h-4 w-4", colorClass)} />;
    }
    
    // Ícone padrão de refresh
    return <RefreshCw className={cn("h-4 w-4", colorClass)} />;
  };

  const getStatusColor = () => {
    // Para o botão completo (não usado no modo iconOnly)
    switch (status) {
      case 'success':
        return 'text-success border-success/20 hover:bg-success/10';
      case 'error':
        return 'text-destructive border-destructive/20 hover:bg-destructive/10';
      default:
        // Aplicar cor baseada no tempo também ao botão completo
        if (dataFreshness) {
          if (dataFreshness.minutesSinceUpdate <= 10) {
            return '';
          } else if (dataFreshness.minutesSinceUpdate <= 30) {
            return 'text-yellow-500 border-yellow-500/20 hover:bg-yellow-500/10';
          } else {
            return 'text-destructive border-destructive/20 hover:bg-destructive/10';
          }
        }
        return '';
    }
  };

  const getLastUpdateText = () => {
    if (!dataFreshness || !dataFreshness.lastUpdate) return 'Nunca atualizado';
    
    return formatDistanceToNow(dataFreshness.lastUpdate, {
      addSuffix: true,
      locale: ptBR,
    });
  };

  const getFreshnessIndicator = () => {
    if (!dataFreshness) return null;
    
    if (dataFreshness.minutesSinceUpdate > 60) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertTriangle className="h-4 w-4 text-amber-500" />
          </TooltipTrigger>
          <TooltipContent>
            <p>Dados desatualizados há {Math.round(dataFreshness.minutesSinceUpdate)} minutos</p>
            <p className="text-xs">Clique em atualizar para obter dados mais recentes</p>
          </TooltipContent>
        </Tooltip>
      );
    } else if (dataFreshness.minutesSinceUpdate > 30) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertCircle className="h-4 w-4 text-yellow-500" />
          </TooltipTrigger>
          <TooltipContent>
            <p>Dados podem estar desatualizados</p>
          </TooltipContent>
        </Tooltip>
      );
    }
    
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Check className="h-4 w-4 text-green-500" />
        </TooltipTrigger>
        <TooltipContent>
          <p>Dados atualizados</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  // Get tooltip message based on status
  const getTooltipMessage = () => {
    if (isRefreshing) {
      return 'Atualizando dados...';
    }
    
    if (status === 'error') {
      return 'Erro ao atualizar. Clique para tentar novamente';
    }
    
    if (status === 'success') {
      return 'Dados atualizados com sucesso!';
    }
    
    if (dataFreshness) {
      if (dataFreshness.minutesSinceUpdate <= 10) {
        return 'Dados atualizados';
      } else if (dataFreshness.minutesSinceUpdate <= 30) {
        return `Dados de ${Math.round(dataFreshness.minutesSinceUpdate)} minutos atrás`;
      } else {
        return `⚠️ Dados desatualizados (${Math.round(dataFreshness.minutesSinceUpdate)} min)`;
      }
    }
    
    return 'Clique para atualizar dados';
  };

  // Return icon-only version if requested
  if (iconOnly) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={cn(
                "transition-all",
                className
              )}
            >
              {getStatusIcon()}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-1">
              <p className="font-medium">{getTooltipMessage()}</p>
              <p className="text-xs text-muted-foreground">{getLastUpdateText()}</p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Full version with text and indicators
  return (
    <TooltipProvider delayDuration={0}>
      <div className={cn("flex items-center gap-2", className)}>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className={cn(
            "gap-2 transition-all",
            getStatusColor()
          )}
          title="Clique para processar os dados mais recentes"
        >
          {getStatusIcon()}
          <span className="hidden sm:inline">
            {isRefreshing ? 'Atualizando...' : 'Atualizar Dados'}
          </span>
        </Button>

        {getFreshnessIndicator()}

        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-xs text-muted-foreground">
              Última atualização: {getLastUpdateText()}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {dataFreshness?.lastUpdate 
                ? `Atualizado em ${dataFreshness.lastUpdate.toLocaleString('pt-BR')}`
                : 'Dados nunca foram atualizados'
              }
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}